import {
  useCallback,
  useMemo,
  useState,
  type Dispatch,
  type ReactElement,
  type ReactNode,
  type SetStateAction,
} from 'react'
import { isDefined, isEmpty, isIncludedIn, isNotEmpty, pluralize, sameArray, unique } from '@terros/common'
import { useDisclosure } from '@mantine/hooks'
import { Group, Modal, Stack, TextInput } from '@mantine/core'
import { useTranslation } from '@front/model'
import { groupColumns } from '../util'
import { type TableColumnDef } from '../types'
import { TDrawer } from '../../terros'
import { ButtonCancel, ButtonSave, TButton } from '../../buttons'
import { ToggleColumn } from './ToggleColumn'
import { SingleColumns } from './SingleColumns'
import { GroupedColumns } from './GroupedColumns'

type Props<T extends TableColumnDef> = ToggleTableColumnsProps<T> & {
  columns: T[]
}

export type ToggleTableColumnsProps<T extends TableColumnDef> = {
  label?: string
  title?: string
  changeOnSubmit?: boolean
  zIndex?: number
  selectedColumns: string[]
  setSelectedColumns: (columns: T['id'][]) => void
  renderColumnToggle?: (props: CustomRenderColumnProps<T>) => ReactNode
  reorderable?: boolean
  presentation?: 'drawer' | 'modal'
  disabled?: boolean
}

export type CustomRenderColumnProps<T extends TableColumnDef> = {
  column: T
  setSelectedColumns: Dispatch<SetStateAction<T['id'][]>>
  selectedColumns: T['id'][]
  defaultToggle: ReactElement
}

export const canDismissOnOutsideAction = (
  changeOnSubmit: boolean | undefined,
  localSelectedColumns: string[],
  savedSelectedColumns: string[]
): boolean => !changeOnSubmit || sameArray(localSelectedColumns, savedSelectedColumns)

export const ToggleColumns = <T extends TableColumnDef>({
  columns,
  label,
  title,
  changeOnSubmit,
  reorderable,
  presentation = 'drawer',
  zIndex,
  disabled,
  ...props
}: Props<T>): ReactElement => {
  const { t } = useTranslation()
  const resolvedTitle = title ?? t('Columns')
  const [opened, { open, close }] = useDisclosure(false)

  const [localSelectedColumns, _setLocalSelectedColumns] = useState(props.selectedColumns)

  const selectedColumns = changeOnSubmit ? localSelectedColumns : props.selectedColumns

  const handleSelectedColumns: Dispatch<SetStateAction<T['id'][]>> = useCallback(
    (input): void => {
      if (changeOnSubmit) {
        _setLocalSelectedColumns((prev) => (typeof input === 'function' ? input(prev) : input))
        return
      }

      const newValue = typeof input === 'function' ? input(selectedColumns) : input
      props.setSelectedColumns(newValue)
    },
    [changeOnSubmit, props, _setLocalSelectedColumns, selectedColumns]
  )

  const toggleColumn = useCallback(
    (column: T['id'], checked: boolean): void => {
      if (checked) {
        handleSelectedColumns((prev) => unique([...prev, column]))
      } else {
        handleSelectedColumns((prev) => prev.filter((e) => e !== column))
      }
    },
    [handleSelectedColumns]
  )

  const handleChangeOnSubmit = useCallback(() => {
    props.setSelectedColumns(localSelectedColumns)
    close()
  }, [close, localSelectedColumns, props])

  const [search, setSearch] = useState('')

  const toggleableColumns = columns
    .filter((column) => column.toggleable)
    .filter((column) => {
      const searchArr = [column.label, column.description, column.group, column.id]
      const lower = search.toLowerCase()
      return searchArr.filter(isDefined).some((str) => isIncludedIn(str, lower))
    })

  const groupedColumns = useMemo(
    () => groupColumns(toggleableColumns, selectedColumns),
    [selectedColumns, toggleableColumns]
  )

  const isReorderActive = reorderable && isEmpty(search) && isEmpty(groupedColumns.groups)

  const renderColumnToggle = useCallback(
    (column: T) => {
      const enabled = selectedColumns.includes(column.id)
      const setEnabled = (checked: boolean): void => toggleColumn(column.id, checked)
      const defaultToggle = <ToggleColumn column={column} enabled={enabled} key={column.id} setEnabled={setEnabled} />
      return props.renderColumnToggle
        ? props.renderColumnToggle({
            column,
            setSelectedColumns: handleSelectedColumns,
            defaultToggle,
            selectedColumns,
          })
        : defaultToggle
    },
    [selectedColumns, props, toggleColumn, handleSelectedColumns]
  )

  const resolvedLabel = useMemo(() => {
    if (isNotEmpty(label)) return label
    return t('{{count}} {{pluralizedColumn}} selected', {
      count: selectedColumns.length,
      pluralizedColumn: pluralize(selectedColumns, t('column'), t('columns')),
    })
  }, [label, selectedColumns, t])

  const toggleGroup = useCallback(
    (group: string, enabled: boolean) => {
      const columns =
        group === 'other' ? groupedColumns.other.columns : (groupedColumns.groups.get(group)?.columns ?? [])
      const columnIds = columns.map((col) => col.id)
      handleSelectedColumns((prev) => {
        if (enabled) {
          return unique([...prev, ...columnIds])
        } else {
          return prev.filter((col) => !columnIds.includes(col))
        }
      })
    },
    [groupedColumns, handleSelectedColumns]
  )

  const content = (
    <Stack gap='sm'>
      <TextInput placeholder={t('Search columns')} value={search} onChange={(e) => setSearch(e.currentTarget.value)} />
      {isNotEmpty(groupedColumns.groups) ? (
        <GroupedColumns
          {...groupedColumns}
          renderColumn={renderColumnToggle}
          selectedColumns={selectedColumns}
          toggleGroup={toggleGroup}
        />
      ) : (
        <SingleColumns
          columns={groupedColumns.other.columns}
          renderColumn={renderColumnToggle}
          reorderable={isReorderActive}
          selectedColumns={selectedColumns}
          setSelectedColumns={handleSelectedColumns}
        />
      )}
    </Stack>
  )

  const submitActions = changeOnSubmit && (
    <>
      <ButtonCancel onClick={close} />
      <ButtonSave onClick={handleChangeOnSubmit} />
    </>
  )

  const canDismiss = canDismissOnOutsideAction(changeOnSubmit, localSelectedColumns, props.selectedColumns)

  if (presentation === 'modal') {
    return (
      <>
        <TButton disabled={disabled} icon='table' variant='gray' onClick={open}>
          {resolvedLabel}
        </TButton>
        <Modal
          centered
          closeOnClickOutside={canDismiss}
          closeOnEscape={canDismiss}
          opened={opened}
          title={resolvedTitle}
          withCloseButton={!changeOnSubmit}
          onClose={close}
        >
          {content}
          {changeOnSubmit && <Group justify='end'>{submitActions}</Group>}
        </Modal>
      </>
    )
  }

  return (
    <>
      <TButton disabled={disabled} icon='table' variant='gray' onClick={open}>
        {resolvedLabel}
      </TButton>
      <TDrawer
        closeOnClickOutside={canDismiss}
        closeOnEscape={canDismiss}
        hideCloseButton={changeOnSubmit}
        opened={opened}
        position='right'
        title={resolvedTitle}
        zIndex={zIndex}
        headerActions={submitActions}
        onClose={close}
      >
        {content}
      </TDrawer>
    </>
  )
}
